import type { GetSessionSnapshotResponse } from "@nlc/shared-types";

import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import type { FileVersionMetadata } from "../types/fileVersionMetadata";

import { createMetadataFromAttachmentName } from "../utils/sessionFileNaming";

export function carryOverMetadataOnAttach(
  metadataMap: Map<string, FileVersionMetadata>,
  clientId: string,
  attachmentId: number,
): boolean {
  const metadata = metadataMap.get(clientId);
  const attachmentKey = String(attachmentId);

  if (!metadata || metadataMap.has(attachmentKey)) {
    return false;
  }

  metadataMap.set(attachmentKey, metadata);

  return true;
}

export function seedMetadataFromSnapshot(
  metadataMap: Map<string, FileVersionMetadata>,
  snapshot: GetSessionSnapshotResponse,
): boolean {
  let didChange = false;

  for (const entry of snapshot.attachments) {
    const clientId = String(entry.attachment.id);

    if (!metadataMap.has(clientId)) {
      metadataMap.set(
        clientId,
        createMetadataFromAttachmentName(entry.attachment.name),
      );
      didChange = true;
    }
  }

  return didChange;
}

interface UseSessionFileMetadataReturn {
  carryOverOnAttach: (clientId: string, attachmentId: number) => boolean;
  clearMetadata: () => void;
  fileMetadataRef: MutableRefObject<Map<string, FileVersionMetadata>>;
  metadataVersion: number;
  updateFileMetadata: (
    clientId: string,
    patch: Partial<FileVersionMetadata>,
  ) => void;
}

export function useSessionFileMetadata(
  snapshot: GetSessionSnapshotResponse | undefined,
): UseSessionFileMetadataReturn {
  const fileMetadataRef = useRef<Map<string, FileVersionMetadata>>(new Map());
  const [metadataVersion, setMetadataVersion] = useState(0);

  useEffect(() => {
    if (!snapshot) return;

    const didChange = seedMetadataFromSnapshot(
      fileMetadataRef.current,
      snapshot,
    );

    if (didChange) {
      setMetadataVersion((version) => version + 1);
    }
  }, [snapshot]);

  const updateFileMetadata = useCallback(
    (clientId: string, patch: Partial<FileVersionMetadata>) => {
      const current = fileMetadataRef.current.get(clientId) ?? {
        displayBaseName: "",
        previousVersionId: null,
      };

      fileMetadataRef.current.set(clientId, { ...current, ...patch });
      setMetadataVersion((version) => version + 1);
    },
    [],
  );

  const clearMetadata = useCallback(() => {
    fileMetadataRef.current.clear();
    setMetadataVersion((version) => version + 1);
  }, []);

  const carryOverOnAttach = useCallback(
    (clientId: string, attachmentId: number) => {
      const carried = carryOverMetadataOnAttach(
        fileMetadataRef.current,
        clientId,
        attachmentId,
      );

      if (carried) {
        setMetadataVersion((version) => version + 1);
      }

      return carried;
    },
    [],
  );

  return {
    carryOverOnAttach,
    clearMetadata,
    fileMetadataRef,
    metadataVersion,
    updateFileMetadata,
  };
}
